import { useState } from 'react';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Plus, Trash2, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import { PAYMENT_METHODS, formatMoneyFull } from '../../../utils/bankConstants';

const RechargesTab = ({ bank, vendor, canEdit, addRecharge, deleteRecharge }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount: '', commission: '', paymentMethod: 'PhonePe', date: '', notes: '' });

  const recharges = bank.recharges || [];
  const stats = {
    count: recharges.length,
    totalAmount: recharges.reduce((sum, r) => sum + (r.amount || 0), 0),
    totalCommission: recharges.reduce((sum, r) => sum + (r.commission || 0), 0)
  };

  const handleSubmit = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Please enter a valid recharge amount'); return; }
    try {
      await addRecharge(vendor.id, bank.id, {
        amount: Number(form.amount), commission: Number(form.commission) || 0,
        paymentMethod: form.paymentMethod, date: form.date, notes: form.notes
      });
      toast.success('Recharge added');
      setForm({ amount: '', commission: '', paymentMethod: 'PhonePe', date: '', notes: '' });
      setShowForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add recharge');
    }
  };

  const handleDelete = async (rechargeId) => {
    try {
      await deleteRecharge(vendor.id, bank.id, rechargeId);
      toast.success('Recharge deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete recharge');
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
          <div className="text-xs text-indigo-600 font-semibold">Total Recharges</div>
          <div className="text-2xl font-bold text-indigo-700 mt-1" data-testid="recharge-count">{stats.count}</div>
        </div>
        <div className="p-4 bg-green-50 rounded-lg border border-green-100">
          <div className="text-xs text-green-600 font-semibold">Total Amount</div>
          <div className="text-xl font-bold text-green-700 mt-1" data-testid="recharge-total-amount">{formatMoneyFull(stats.totalAmount)}</div>
        </div>
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
          <div className="text-xs text-amber-600 font-semibold">Total Commission</div>
          <div className="text-xl font-bold text-amber-700 mt-1" data-testid="recharge-total-commission">{formatMoneyFull(stats.totalCommission)}</div>
        </div>
      </div>

      {canEdit && (
        <Card className="p-4">
          {!showForm ? (
            <Button onClick={() => setShowForm(true)} className="gap-2 w-full" variant="outline" data-testid="add-recharge-btn"><Plus className="w-4 h-4" /> Add Recharge</Button>
          ) : (
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-700">New Recharge</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Recharge Amount *</Label><Input data-testid="recharge-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" /></div>
                <div><Label className="text-xs">Commission Charged</Label><Input data-testid="recharge-commission" type="number" step="0.01" value={form.commission} onChange={(e) => setForm(p => ({ ...p, commission: e.target.value }))} placeholder="0.00" /></div>
                <div><Label className="text-xs">Payment Method</Label>
                  <Select value={form.paymentMethod} onValueChange={(val) => setForm(p => ({ ...p, paymentMethod: val }))}>
                    <SelectTrigger data-testid="recharge-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Date</Label><Input data-testid="recharge-date" type="date" value={form.date} onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))} /></div>
                <div className="col-span-2"><Label className="text-xs">Notes</Label><Input data-testid="recharge-notes" value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" /></div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setForm({ amount: '', commission: '', paymentMethod: 'PhonePe', date: '', notes: '' }); }}>Cancel</Button>
                <Button size="sm" onClick={handleSubmit} data-testid="submit-recharge-btn" className="gap-1"><IndianRupee className="w-3 h-3" />Add Recharge</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left p-3 text-xs font-semibold text-slate-600">Date</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-600">Amount</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-600">Commission</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-600">Payment Method</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-600">Notes</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-600">Added By</th>
                {canEdit && <th className="text-right p-3 text-xs font-semibold text-slate-600">Action</th>}
              </tr>
            </thead>
            <tbody>
              {recharges.length > 0 ? (
                [...recharges].reverse().map((r, idx) => (
                  <tr key={r.id || `recharge-${r.date}-${idx}`} className="border-b hover:bg-slate-50" data-testid={`recharge-row-${idx}`}>
                    <td className="p-3">{r.date || '-'}</td>
                    <td className="p-3 font-semibold text-green-700">{formatMoneyFull(r.amount)}</td>
                    <td className="p-3 text-amber-700">{formatMoneyFull(r.commission)}</td>
                    <td className="p-3"><Badge variant="outline">{r.paymentMethod}</Badge></td>
                    <td className="p-3 text-slate-600">{r.notes || '-'}</td>
                    <td className="p-3 text-slate-600 text-xs">{r.createdByName || '-'}</td>
                    {canEdit && (
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => handleDelete(r.id)} data-testid={`delete-recharge-${idx}`}><Trash2 className="w-3 h-3" /></Button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr><td colSpan={canEdit ? 7 : 6} className="p-8 text-center text-slate-500">No recharges recorded yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default RechargesTab;
